import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getLiveStatus } from "@/lib/live.functions";
import { getMyProfile, updateLiveNotificationPref } from "@/lib/user.functions";
import { Radio, Bell, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/ao-vivo")({
  head: () => ({ meta: [{ title: "Ao vivo · Beit Tefilah Rechovot" }] }),
  component: LivePage,
});

type Pref = "off" | "shabbat" | "all";

function LivePage() {
  const liveFn = useServerFn(getLiveStatus);
  const profileFn = useServerFn(getMyProfile);
  const prefFn = useServerFn(updateLiveNotificationPref);
  const qc = useQueryClient();

  const { data: live, isLoading } = useQuery({
    queryKey: ["current-live"],
    queryFn: () => liveFn(),
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const { data: profile } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn() });

  const mutation = useMutation({
    mutationFn: (pref: Pref) => prefFn({ data: { pref } }),
    onSuccess: (_r, pref) => {
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success(
        pref === "off"
          ? "Notificações desativadas."
          : pref === "shabbat"
            ? "Você receberá avisos apenas nas transmissões de Shabat."
            : "Você receberá avisos de todas as transmissões ao vivo.",
      );
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível salvar."),
  });

  const currentPref: Pref = (profile?.live_notification_pref as Pref) ?? "off";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-serif text-3xl font-semibold text-primary">
          <Radio className="h-7 w-7 text-destructive" /> Transmissão ao vivo
        </h1>
        <p className="mt-1 text-foreground/75">
          Assista às transmissões ao vivo da Beit Tefilah Rechovot direto por aqui.
        </p>
      </div>

      {isLoading ? (
        <div className="h-96 animate-pulse rounded-2xl bg-secondary" />
      ) : live?.live ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg bg-destructive/10 px-4 py-2 text-sm font-semibold text-destructive">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
            </span>
            AO VIVO AGORA
          </div>
          <div className="aspect-video overflow-hidden rounded-2xl border border-border bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${live.videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={live.title}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <h2 className="font-serif text-xl font-semibold text-primary">{live.title}</h2>
          <a
            href={`https://www.youtube.com/watch?v=${live.videoId}`}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm font-medium text-primary underline underline-offset-4"
          >
            Assistir no YouTube
          </a>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-primary">
            Não há transmissão ao vivo neste momento
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-foreground/75">
            Acompanhe a Transmissão ao Vivo nos horários listados abaixo. Fique atento para os
            horários das festividades, que podem ser diferenciados.
          </p>
          {live && !live.live && live.reason !== "offline" && (
            <p className="mx-auto mt-4 max-w-xl text-sm text-destructive">
              Não foi possível confirmar o status agora
              {live.reason === "not_configured"
                ? ": integração com o YouTube não configurada."
                : "."}{" "}
              Tente novamente em instantes ou assista pelo canal no YouTube.
            </p>
          )}
        </div>
      )}

      <section>
        <h3 className="font-serif text-xl font-semibold text-primary">
          Horários da Transmissão ao Vivo
        </h3>
        <p className="mt-1 text-sm text-foreground/70">Todos os horários em Brasília (BRT).</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ScheduleCard
            title="Shacharit"
            subtitle="Orações da manhã"
            slots={[
              { day: "Segunda à Sexta", time: "6h" },
              { day: "Sábado", time: "9h" },
              { day: "Domingo", time: "8h" },
            ]}
          />
          <ScheduleCard
            title="Cabalat Shabat"
            subtitle="Recebimento do Sábado"
            slots={[{ day: "Sexta-feira", time: "19h" }]}
          />
          <ScheduleCard
            title="Serviço da Torá"
            subtitle="Estudo das Parashiot"
            slots={[{ day: "Sábado", time: "10h" }]}
          />
          <ScheduleCard
            title="Arvit e Havdalah"
            subtitle="Orações e Encerramento do Sábado"
            slots={[{ day: "Sábado", time: "17h" }]}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
            <Bell className="h-5 w-5 text-gold" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-lg font-semibold text-primary">
              Notificações por e-mail
            </h3>
            <p className="mt-1 text-sm text-foreground/75">
              Escolha se deseja receber um aviso por e-mail sempre que uma transmissão ao vivo
              começar.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {[
                { v: "off" as Pref, label: "Desativado", desc: "Não receber avisos" },
                { v: "shabbat" as Pref, label: "Apenas Shabat", desc: "Só transmissões de sábado" },
                { v: "all" as Pref, label: "Todas as transmissões", desc: "Qualquer live" },
              ].map((opt) => {
                const active = currentPref === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    disabled={mutation.isPending}
                    onClick={() => mutation.mutate(opt.v)}
                    className={cn(
                      "rounded-lg border p-3 text-left text-sm transition-all",
                      active
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/50 hover:bg-secondary/40",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground">{opt.label}</span>
                      {active && <Check className="h-4 w-4 text-primary" />}
                    </div>
                    <span className="mt-1 block text-xs text-foreground/70">{opt.desc}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-foreground/60">
              {live?.emailNotificationsActive === false
                ? "Os avisos por e-mail ainda estão suspensos: sua preferência fica salva e passa a valer assim que os envios aos membros forem liberados."
                : "Os avisos por e-mail estão ativos: você recebe apenas conforme a opção escolhida acima e pode alterá-la quando quiser."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ScheduleCard({
  title,
  subtitle,
  slots,
}: {
  title: string;
  subtitle: string;
  slots: { day: string; time: string }[];
}) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5">
      <h4 className="font-serif text-xl font-semibold text-primary">{title}</h4>
      <p className="mt-1 text-sm text-foreground/70">{subtitle}</p>
      <div className="mt-4 flex flex-1 flex-col gap-3">
        {slots.map((s, i) => (
          <div
            key={`${s.day}-${s.time}`}
            className={cn(
              "flex flex-col items-center gap-1 py-2",
              i > 0 && "border-t border-dashed border-border",
            )}
          >
            <span className="rounded-full bg-secondary px-3 py-0.5 text-xs font-medium text-foreground/80">
              {s.day}
            </span>
            <span className="font-serif text-3xl font-semibold text-primary">{s.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
